import { stripEmDashes } from "@/lib/text";
/**
 * Deep cleanup for scraped article HTML.
 * Removes: all links, embedded widgets (tweets, price/charts, iframes, scripts),
 * publisher furniture (most-popular rails, newsletters, disclaimers, bios) and
 * duplicated blocks. DOM based so nested markup is removed safely.
 */

const FURNITURE_PATTERNS = [
  /^most\s+popular/i,
  /^most\s+read/i,
  /^read\s+(more|next|also)/i,
  /^related(\s+(stories|articles|reading|posts))?:?$/i,
  /^recommended(\s+for\s+you)?/i,
  /^you\s+m(ay|ight)\s+also\s+like/i,
  /^more\s+from\b/i,
  /^more\s+(stories|coverage|on\s+this)/i,
  /^latest\s+(news|stories|from)/i,
  /^trending\b/i,
  /^sign\s+up\b/i,
  /^subscribe\b/i,
  /^newsletter/i,
  /^advertisement$/i,
  /^sponsored(\s+content)?$/i,
  /^share\s+this\s+article/i,
  /^follow\s+us\b/i,
  /^in\s+this\s+article:?$/i,
  /^disclaimer\b/i,
  /^this\s+article\s+is\s+for\s+informational/i,
  /^(originally|this\s+(post|story|article))\s+(appeared|published)/i,
  /^about\s+the\s+author/i,
  /^(leave|post)\s+a\s+comment/i,
  /^comments?\s*\(\d+\)$/i,
  /^we\s+use\s+cookies/i,
  /^(download|get)\s+(the|our)\s+app/i,
  /^(photo|image|getty|reuters|credit)\s*[:|]/i,
  /^view\s+on\s+(x|twitter)$/i,
  /^see\s+more\s+on\s+(x|twitter)$/i,
  /^(loading|advertisement)\s*\.{0,3}$/i,
  /^add\s+(us|.{0,40})\s+on\s+google/i,
  /^add\s+.{0,40}\s+on\s+google\s+for\s+similar\s+content/i,
  /^follow\s+(us|.{0,40})\s+on\b/i,
  /^by\s+[\p{L}'’.\- ]{2,40}$/iu,
  /^(written|reported|edited|reviewed)\s+by\b/i,
  /^(editor|senior\s+editor|staff\s+writer|correspondent|contributing\s+writer|reporter)\b.{0,60}$/i,
  /^(updated|published|posted)\b.{0,60}$/i,
  /^[A-Z][a-z]+ \d{1,2}, \d{4}(,| at)?\s?.{0,20}(am|pm)?\s?(et|pt|ct|gmt|utc)?\.?$/i,
  /\bdown\s+arrow\s+button\s+icon\b/i,
  /^(share|copy\s+link|print|save)$/i,
  /^\d+\s+min(ute)?\s+read$/i,
  /^listen\s+to\s+(this\s+)?article/i,
  /^(getty|bloomberg|reuters|afp|ap)\s*\/\s*(contributor|images)?$/i,
];


const FURNITURE_ATTR =
  /(most-?popular|most-?read|read-?next|related|recirc|recommend|newsletter|subscribe|promo|advert|sponsored|trending|social-?share|outbrain|taboola|disclaimer|author-?bio|comments?-|cookie|app-?download|paywall)/i;

const WIDGET_ATTR =
  /(tradingview|coingecko|coinmarketcap|coinbase-?widget|crypto-?widget|price-?widget|chart-?widget|market-?widget|ticker-?widget|widget-?container|embed-?container|twitter-?(tweet|widget)|tweet-?embed|instagram-media|tiktok-embed|fb-post|iframely|embedly)/i;

const isFurnitureLabel = (text: string) => {
  const t = text.trim();
  if (!t || t.length > 80) return false;
  return FURNITURE_PATTERNS.some((re) => re.test(t));
};

const isIndexMarker = (el: Element) =>
  /^\d{1,2}[.)]?$/.test((el.textContent || "").trim()) && el.querySelectorAll("img").length === 0;

const hasMedia = (el: Element) => !!el.querySelector("img,figure,video,picture");

const norm = (s: string) =>
  s
    .replace(/\s+/g, " ")
    .replace(/[’‘]/g, "'")
    .replace(/[“”]/g, '"')
    .trim()
    .toLowerCase();

export const cleanArticleHtml = (html: string, title?: string): string => {
  if (!html) return html;
  html = stripEmDashes(html);
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return html;

  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(`<div id="__root">${html}</div>`, "text/html");
  } catch {
    return html;
  }
  const root = doc.getElementById("__root");
  if (!root) return html;

  // 1) Hard-remove embeds, widgets and scripts of any kind
  root
    .querySelectorAll(
      "script,iframe,embed,object,form,noscript,blockquote.twitter-tweet,blockquote.tiktok-embed,blockquote.instagram-media,[data-tweet-id],[data-tweet-url],[data-widget],[data-embed]"
    )
    .forEach((el) => el.remove());

  // 2) Containers flagged as widgets or publisher furniture by class/id/data-*
  root.querySelectorAll("[class],[id],[data-module],[data-component]").forEach((el) => {
    if (!el.isConnected) return;
    const attrs = `${el.getAttribute("class") || ""} ${el.getAttribute("id") || ""} ${
      el.getAttribute("data-module") || ""
    } ${el.getAttribute("data-component") || ""}`;
    if (WIDGET_ATTR.test(attrs) || FURNITURE_ATTR.test(attrs)) el.remove();
  });

  // 3) Furniture headings — drop the heading and everything after it in its parent
  root.querySelectorAll("h1,h2,h3,h4,h5,h6,strong,b,p,div,span,li").forEach((el) => {
    if (!el.isConnected) return;
    if (!isFurnitureLabel(el.textContent || "")) return;
    if (/^H[1-6]$/.test(el.tagName)) {
      let node: Element | null = el;
      const toRemove: Element[] = [];
      while (node) {
        toRemove.push(node);
        node = node.nextElementSibling;
      }
      toRemove.forEach((n) => n.remove());
    } else {
      el.remove();
    }
  });

  // 4) Remove every link along with its text
  root.querySelectorAll("a").forEach((a) => a.remove());

  // 5) Standalone numeric index markers (1, 2, 3 rails)
  root.querySelectorAll("p,div,span,h1,h2,h3,h4,h5,h6,li").forEach((el) => {
    if (!el.isConnected) return;
    if (el.children.length === 0 && isIndexMarker(el)) el.remove();
  });

  // 6) Remove a repeated article title inside the body
  if (title) {
    const t = norm(title);
    root.querySelectorAll("p,h1,h2,h3,h4,h5,h6,strong,b").forEach((el) => {
      if (!el.isConnected || hasMedia(el)) return;
      const text = norm(el.textContent || "");
      if (text && (text === t || (t.length > 30 && (t.startsWith(text) || text.startsWith(t)) && Math.abs(t.length - text.length) < 15))) {
        el.remove();
      }
    });
  }

  // 7) Collapse duplicated text blocks (scrapers repeat bylines/headline rails)
  const seen = new Set<string>();
  root.querySelectorAll("p,h1,h2,h3,h4,h5,h6,li,blockquote").forEach((el) => {
    if (!el.isConnected || hasMedia(el)) return;
    const key = norm(el.textContent || "");
    if (key.length < 4) return;
    if (seen.has(key)) el.remove();
    else seen.add(key);
  });


  // 7) Drop blocks left empty or containing only punctuation/separators
  for (let pass = 0; pass < 2; pass++) {
    root.querySelectorAll("p,div,section,ul,ol,li,span,blockquote,figure").forEach((el) => {
      if (!el.isConnected) return;
      if (hasMedia(el)) return;
      const text = (el.textContent || "").replace(/\s+/g, " ").trim();
      if (!text || /^[\s.,;:—–\-|•·>/\\()[\]]+$/.test(text)) el.remove();
    });
  }

  return root.innerHTML;
};

/** Back-compat alias for the earlier, narrower cleanup. */
export const stripRecirculation = cleanArticleHtml;

export default cleanArticleHtml;
